import { FunctionComponent, useMemo, useEffect } from "react";
import {
  APIProvider,
  Map as GoogleMap,
  useMap,
} from "@vis.gl/react-google-maps";
import { PlanMarker } from "./elements/PlanMarker";
import { Location } from "@dto/TripBuilder";
import "./InlineMap.css";
interface InlineMapProps {
  locations: Location[];
}

const boundPadding = 120;

const getBoundsFromLocations = (locations: Location[]) => {
  if (locations.length === 0) {
    const lat = Number(VisitWidget.settings.defaultCoordinates.latitude);
    const lng = Number(VisitWidget.settings.defaultCoordinates.longitude);
    return {
      north: lat,
      south: lat,
      east: lng,
      west: lng,
    };
  }
  let north = locations[0].latitude;
  let south = locations[0].latitude;
  let east = locations[0].longitude;
  let west = locations[0].longitude;
  for (const loc of locations) {
    if (loc.latitude > north) {
      north = loc.latitude;
    }
    if (loc.latitude < south) {
      south = loc.latitude;
    }
    if (loc.longitude > east) {
      east = loc.longitude;
    }
    if (loc.longitude < west) {
      west = loc.longitude;
    }
  }
  return { north, south, east, west, padding: boundPadding };
};

const FitBounds: FunctionComponent<{ locations: Location[] }> = ({
  locations,
}) => {
  const map = useMap();
  useEffect(() => {
    if (!map || locations.length === 0) {
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    locations.forEach((loc) => {
      bounds.extend({ lat: loc.latitude, lng: loc.longitude });
    });
    map.fitBounds(bounds, boundPadding);
  }, [map, locations]);
  return null;
};

const InlineMap: FunctionComponent<InlineMapProps> = ({ locations }) => {
  const bounds = useMemo(() => getBoundsFromLocations(locations), [locations]);
  const mapStyles = useMemo(
    () => [
      ...(VisitWidget.mapTemplate ?? []),
      {
        featureType: "poi",
        elementType: "all",
        stylers: [{ visibility: "off" }],
      },
    ],
    [],
  );

  return (
    <div className="inline-map-container" data-testid="inline-map">
      <APIProvider apiKey={VisitWidget.settings.mapBrowserApiKey}>
        <GoogleMap
          gestureHandling="none"
          disableDefaultUI={true}
          keyboardShortcuts={false}
          clickableIcons={false}
          cameraControl={false}
          mapId={undefined}
          defaultBounds={bounds}
          scaleControl={false}
          mapTypeControl={false}
          styles={mapStyles}
          style={{
            borderRadius: "8px",
          }}>
          <FitBounds locations={locations} />
          {locations.map((location, idx) => (
            <PlanMarker
              key={`${location.latitude}-${location.longitude}`}
              coordinates={{
                latitude: location.latitude,
                longitude: location.longitude,
              }}
              ordinal={idx + 1}
            />
          ))}
        </GoogleMap>
      </APIProvider>
    </div>
  );
};

export { InlineMap };
